import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import '@fortawesome/fontawesome-free/css/all.min.css';
import "bootstrap/dist/css/bootstrap.min.css"
import "bootstrap"
import 'bootstrap/dist/js/bootstrap.bundle.min.js'
import "bootstrap/dist/js/bootstrap.min.js";
import "./assets/scss/app.scss"
import Notifications from '@kyvg/vue3-notification'
import 'core-js/features/object/has-own';

// Suppress ResizeObserver loop error (benign browser warning)
const resizeObserverErr = (e: ErrorEvent) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
    }
};
window.addEventListener('error', resizeObserverErr);

import VueFeather from "vue-feather";
import { createI18n } from 'vue-i18n'
import Breadcrumbs from '@/layout/theBreadcrumbs.vue';
import type menu from "@/types/menu";
import StudentMenu from "@/core/data/studentMenu.json";
import StaffMenu from "@/core/data/staffMenu.json";
 
import Auth from './API/Auth'
import DirectiveScannerPlugin from './directiveScannerPlugin'
import { toast } from './utils/helpers'
import language from "@/API/Language/Language";
import translate from "@/API/Translate/Translate";
import enLocale from "@/locales/en.json";
import aeLocale from "@/locales/ae.json";
import { useMenuStore } from "./store/menu";
import { useLoaderStore } from "./store/loader";
import { setupAxiosLoader } from "./utils/axiosLoader";
import { isStudentUser, isStaffUser } from "./utils/portalAccess";

import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

const vuetify = createVuetify({
    components,
    directives,
})

// TypeScript interfaces
interface LanguageItem {
    id: string;
    code: string;
    name: string;
    order: number;
    rtl: string;
    flag: string;
}

interface Messages {
    [key: string]: {
        [key: string]: {
            [key: string]: string
        }
    }
}

interface TranslationEntry {
    [key: string]: string;
}

interface TranslationGroup {
    name: string;
    en: TranslationEntry[];
    ar: TranslationEntry[];
    [key: string]: any;
    id: string;
    created_at: string;
    updated_at: string;
}

interface ProcessedTranslations {
    [key: string]: { // language code
        [key: string]: { // section name
            [key: string]: string // translations
        }
    }
}

type MenuType = "student" | "admin" | "staff";

function cloneMenuData(items: menu[] = []): menu[] {
    return JSON.parse(JSON.stringify(items));
}

function getStudentMenuItems(): menu[] {
    const data = (StudentMenu as unknown as { data: menu[] }).data || [];
    return cloneMenuData(data);
}

function getStaffMenuItems(): menu[] {
    const data = (StaffMenu as unknown as { data: menu[] }).data || [];
    return cloneMenuData(data);
}

function applyMenuPreset(menuStore: ReturnType<typeof useMenuStore>, type: MenuType) {
    if (type === "student") {
        const studentMenu = getStudentMenuItems();
        if (studentMenu.length) {
            menuStore.setMenu(studentMenu);
            return;
        }
    }
    if (type === "staff") {
        const staffMenu = getStaffMenuItems();
        if (staffMenu.length) {
            menuStore.setMenu(staffMenu);
            return;
        }
    }
    menuStore.resetMenu();
}

function resolveMenuType(
    user: Record<string, any> | null | undefined,
    storedType: string | null
): MenuType {
    if (user) {
        if (isStudentUser(user)) return "student";
        if (isStaffUser(user)) return "staff";
        return "admin";
    }
    if (storedType === "student") return "student";
    if (storedType === "staff") return "staff";
    return "admin";
}

// Initialize APIs
const languageApi = new language();
const translateApi = new translate();

// Function to process translations from API
function processTranslations(translations: TranslationGroup[]): ProcessedTranslations {
    const processed: ProcessedTranslations = {};

    translations.forEach(group => {
        // Get all language codes from the group (excluding non-language properties)
        const langCodes = Object.keys(group).filter(key => 
            !['name', 'id', 'created_at', 'updated_at'].includes(key)
        );

        // Process each language code
        langCodes.forEach(langCode => {
            if (!processed[langCode]) {
                processed[langCode] = {};
            }
            
            if (!processed[langCode][group.name]) {
                processed[langCode][group.name] = {};
            }

            // Process translations for this language and group
            const translations = group[langCode];
            if (Array.isArray(translations)) {
                translations.forEach(trans => {
                    Object.entries(trans).forEach(([key, value]) => {
                        if (typeof value === 'string') {
                            processed[langCode][group.name][key] = value;
                        }
                    });
                });
            }
        });
    });

    return processed;
}

/** Merge local JSON locale (defaults) with API translations; API wins on conflicts. */
function mergeLocaleMessages(
    staticMessages: Record<string, unknown> | undefined,
    apiMessages: Record<string, unknown> | undefined
): Record<string, unknown> {
    const base = staticMessages || {};
    const api = apiMessages || {};
    const keys = new Set([...Object.keys(base), ...Object.keys(api)]);
    const out: Record<string, unknown> = {};
    for (const k of keys) {
        const b = base[k];
        const a = api[k];
        if (
            b &&
            typeof b === "object" &&
            !Array.isArray(b) &&
            a &&
            typeof a === "object" &&
            !Array.isArray(a)
        ) {
            out[k] = { ...(b as object), ...(a as object) };
        } else if (a !== undefined) {
            out[k] = a;
        } else {
            out[k] = b;
        }
    }
    return out;
}

/** Local defaults by language code (ae.json is used for Arabic `ar` / `ae`). */
const STATIC_LOCALES: Record<string, Record<string, unknown>> = {
    en: enLocale as unknown as Record<string, unknown>,
    ar: aeLocale as unknown as Record<string, unknown>,
    ae: aeLocale as unknown as Record<string, unknown>,
};

/** Overrides for admission form labels (wins over DB translate API). */
const ADMISSION_LABEL_OVERRIDES: Record<string, Record<string, string>> = {
    ar: {
        how_know: "كيف تعرفتِ على إعلان القبول في المعهد",
        friend: "عن طريق صديقة",
    },
    ae: {
        how_know: "كيف تعرفتِ على إعلان القبول في المعهد",
        friend: "عن طريق صديقة",
    },
    en: {
        how_know: "How did you learn about the institute admission announcement?",
        friend: "Through a female friend",
    },
};

function applyAdmissionLabelOverrides(messages: Messages): void {
    for (const [locale, overrides] of Object.entries(ADMISSION_LABEL_OVERRIDES)) {
        const bucket = messages[locale];
        if (!bucket) continue;
        const section = bucket.online_admission;
        if (!section || typeof section !== "object" || Array.isArray(section)) {
            bucket.online_admission = { ...overrides };
            continue;
        }
        Object.assign(section as Record<string, string>, overrides);
    }
}

// Function to load language messages
async function loadLanguages(): Promise<{ messages: Messages, defaultLocale: string }> {
    try {
        // Get both languages configuration and translations
        const [languages, translations] = await Promise.all([
            languageApi.getAll() as Promise<LanguageItem[]>,
            translateApi.getAll() as Promise<TranslationGroup[]>
        ]);
        
        // Sort languages by order
        const sortedLanguages = languages.sort((a, b) => Number(a.order) - Number(b.order));

        // Process translations into organized structure
        const processedTranslations = processTranslations(translations);
        
        // Create messages object (merge local en.json / ae.json with API so keys like validation.name_required apply)
        const messages: Messages = {};
        for (const lang of sortedLanguages) {
            const langCode = lang.code.toLowerCase().split('-')[0];
            const staticMsgs = STATIC_LOCALES[langCode];
            const apiMsgs = processedTranslations[langCode];
            if (apiMsgs) {
                messages[lang.code.toLowerCase()] = mergeLocaleMessages(
                    staticMsgs,
                    apiMsgs
                ) as Messages[string];
            } else if (staticMsgs) {
                messages[lang.code.toLowerCase()] = staticMsgs as Messages[string];
            } else {
                console.warn(`No translations found for language: ${lang.name} (${lang.code})`);
                messages[lang.code.toLowerCase()] = {} as Messages[string];
            }
        }
        applyAdmissionLabelOverrides(messages);
        // Get saved language from localStorage, fallback to Arabic
        const savedLangCode = localStorage.getItem('selectedLanguageCode');
        let defaultLocale = 'ar';
        if (savedLangCode && messages[savedLangCode]) {
            defaultLocale = savedLangCode;
        } else if (messages['ar']) {
            defaultLocale = 'ar';
        } else if (messages['ae']) {
            defaultLocale = 'ae';
        } else if (messages['en']) {
            defaultLocale = 'en';
        }
        if (!savedLangCode) {
            localStorage.setItem('selectedLanguageCode', defaultLocale);
        }
        return {
            messages,
            defaultLocale
        };
    } catch (err) {
        console.error('Failed to load languages:', err);
        // Fallback to Arabic if API fails
        const Arabic = await import("@/locales/ae.json");
        const arabicMessages: { [key: string]: { [key: string]: string } } = {};
        Object.entries(Arabic.default).forEach(([section, value]) => {
            arabicMessages[section] = typeof value === 'object' && value !== null ? value as { [key: string]: string } : { value: value as string };
        });
        if (!localStorage.getItem('selectedLanguageCode')) {
            localStorage.setItem('selectedLanguageCode', 'ar');
        }
        return {
            messages: { ar: arabicMessages, ae: arabicMessages },
            defaultLocale: 'ar',
        };
    }
}

let i18n: ReturnType<typeof createI18n>;

// Initialize app after loading languages
async function initializeApp() {
    const { messages, defaultLocale } = await loadLanguages();
    
    i18n = createI18n({
        legacy: false,
        locale: defaultLocale,
        messages,
        fallbackLocale: 'ar'
    });

    const pinia = createPinia();
    const menuStore = useMenuStore(pinia);
    
    // Initialize loader store and setup axios interceptors
    const loaderStore = useLoaderStore(pinia);
    setupAxiosLoader(loaderStore);

    Auth.run();

    const isLoggedIn = await Auth.loggedIn();

    const storedMenuType = localStorage.getItem('activeMenuType');
    const menuType = resolveMenuType(isLoggedIn ? Auth.USER : null, storedMenuType);
    applyMenuPreset(menuStore, menuType);

    if (isLoggedIn) {
        localStorage.setItem('activeMenuType', menuType);
    } else if (!storedMenuType) {
        localStorage.setItem('activeMenuType', 'admin');
    }

    const app = createApp(App);
    app.use(router);
    app.use(pinia);
    app.use(i18n);
    app.use(vuetify);
    app.use(Notifications);
    app.component('Breadcrumbs', Breadcrumbs);
    app.use(DirectiveScannerPlugin);
    app.config.globalProperties.$toast = toast;
    app.mount('#app');
}

// Export i18n instance for use in other files
export { i18n };

// Start the application
initializeApp().catch(console.error);
